/*  iceberg-ui  */
import { WithContext } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Paper } from '@mui/material';

/* Tables */
import { CellContext } from '@tanstack/react-table';

/* Shared */
import {
    AppNameLinkCell,
    DateFormatNoTimeStringCell,
    RiskCell,
    dateFilter,
    lastXDays,
    riskCellFilter
} from 'application/Shared/Components/Cells';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI
} from 'application/Shared/Components/Table';
import { ColumnDef } from 'application/Shared/Components/Table/types';
import {
    sortByRiskFn,
    sortByTimestamp
} from 'application/Shared/Components/TableSortFunctions';
import {
    RecentlyAddedAppsTableContext,
    useRecentlyAddedAppsTableContext
} from 'application/Shared/Components/Tables/RecentlyAddedAppsTableContext';
import { TABLENAMES } from 'application/Shared/Constants';
import { TenantApp } from 'application/Shared/types/tenantApp';
import { subDays } from 'date-fns';

/*  React  */
import React, { ReactElement, useEffect } from 'react';

const ActionsCell = ({ row }: CellContext<TenantApp, unknown>) => (
    <WithContext context={RecentlyAddedAppsTableContext}>
        {({ route, navigate, fromPage, t }) => (
            <KebabMenu
                data-testid={`recentlyAddedAppsTableRowActionsAppID_${row.original?.application?.id}`}
                onClick={(value: string) => {
                    if (value === 'view') {
                        navigate(
                            `${route}/application/${row.original?.application?.id}/details`,
                            {
                                state: {
                                    from: fromPage
                                }
                            }
                        );
                    }
                }}
                options={[
                    {
                        key: 'recentlyAddedAppsTableViewAppBtn',
                        label: t('Common.view'),
                        value: 'view'
                    }
                ]}
            />
        )}
    </WithContext>
);

const columns = ({
    fromPage
}: {
    fromPage: string;
}): ColumnDef<TenantApp, string>[] => [
    {
        accessorKey: 'application.name',
        cell: (props) => <AppNameLinkCell fromPage={fromPage} {...props} />,
        enableColumnFilter: false,
        enableHiding: false,
        header: Header,
        id: 'apps',
        name: 'Common.application',
        size: 300,
        testId: 'application.name'
    },
    {
        accessorKey: 'dateAdded',
        cell: DateFormatNoTimeStringCell,
        filterFn: lastXDays,
        header: Header,
        id: 'dateAdded',
        meta: { filter: dateFilter },
        name: 'Common.dateAdded',
        size: 150,
        sortingFn: sortByTimestamp,
        testId: 'dateAdded'
    },
    {
        accessorKey: 'risk',
        cell: RiskCell,
        filterFn: 'arrIncludesSome',
        header: Header,
        id: 'riskFilter',
        meta: { filter: riskCellFilter },
        name: 'Common.risk',
        size: 150,
        sortingFn: sortByRiskFn,
        testId: 'risk'
    },
    {
        accessorKey: 'actions',
        cell: ActionsCell,
        enableColumnFilter: false,
        enableHiding: false,
        enableSorting: false,
        header: Header,
        id: 'actions',
        maxSize: 50,
        meta: {
            align: 'right',
            header: {
                justifyContent: 'flex-end'
            }
        },
        name: 'Common.actions',
        size: 50,
        testId: 'actions'
    }
];

export const RecentlyAddedAppsTable = ({
    fromPage
}: {
    fromPage: string;
}): ReactElement => {
    const { setFromPage } = useRecentlyAddedAppsTableContext();

    useEffect(() => setFromPage(fromPage), [fromPage, setFromPage]);

    const baseParameters = {
        // Get apps added in the last 30 days
        dateAdded: subDays(new Date(), 30).toISOString(),
        excludeDisabled: true,
        excludePersonal: true,
        includeActiveUser: true, // Otherwise unanalyzed apps are returned
        includeApplication: true,
        includeUncataloged: true,
        includeUncategorizedWorkLogins: true,
        size: -1
    };

    return (
        <ManagedTableV2ContextProvider
            baseParameters={baseParameters}
            columns={columns({ fromPage })}
            getRowId="id"
            rowsApi="/tenantApp"
            mode="client"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled={false}
            sortDirection="DESC"
            sortKey="dateAdded"
            tableName={TABLENAMES.RECENTLY_ADDED_APPS}
        >
            <Paper elevation={0} variant="outlined" sx={{ p: 0 }}>
                <ManagedTableV2UI
                    renderFooter
                    titleDef={{
                        noCount: true,
                        titleKey: 'Common.recently_added_applications'
                    }}
                />
            </Paper>
        </ManagedTableV2ContextProvider>
    );
};
